// src/pages/admin/AdminPayment.jsx

import React, { useState, useEffect, useMemo } from 'react';
import {
    BsCheckLg,
    BsXLg,
    BsEye,
    BsWalletFill,
    BsClock,
    BsCheckCircleFill,
    BsClockHistory,
    BsReceipt,
    BsPersonPlusFill,
    BsXCircleFill,
    BsExclamationTriangle,
    BsInfoCircle
} from 'react-icons/bs';
import { toast } from 'react-toastify';
import api from '../../api/axios';

// ======================================================================
// HELPER DATA
// ======================================================================
const normalizeText = (value) => String(value || '').toLowerCase().trim();

const safeNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const firstValue = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null && value !== '') return value;
    }

    return 0;
};

const formatRupiah = (num) => {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
    }).format(safeNumber(num));
};

const getEntryMembers = (athlete) => {
    if (!athlete) return [];

    if (Array.isArray(athlete.entry_members)) {
        return athlete.entry_members
            .map(member => String(member || '').trim())
            .filter(Boolean);
    }

    if (typeof athlete.entry_members === 'string' && athlete.entry_members.trim() !== '') {
        try {
            const parsed = JSON.parse(athlete.entry_members);

            if (Array.isArray(parsed)) {
                return parsed
                    .map(member => String(member || '').trim())
                    .filter(Boolean);
            }
        } catch (error) {
            return athlete.entry_members
                .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                .map(member => String(member || '').trim())
                .filter(Boolean);
        }
    }

    return [];
};

const getDisplayName = (athlete) => {
    const members = getEntryMembers(athlete);

    if (members.length > 1) {
        return members.join(' / ');
    }

    return athlete?.name || '-';
};

const getPaymentTeam = (payment) => {
    return payment?.team || payment?.Team || null;
};

const getTeamName = (payment) => {
    const team = getPaymentTeam(payment) || payment;

    return (
        team?.name ||
        payment?.team_name ||
        payment?.name ||
        'Unknown Team'
    );
};

const getManagerName = (payment) => {
    const team = getPaymentTeam(payment) || payment;

    return (
        team?.user?.name ||
        team?.User?.name ||
        team?.manager?.name ||
        team?.manager_name ||
        '-'
    );
};

const getManagerPhone = (payment) => {
    const team = getPaymentTeam(payment) || payment;

    return (
        team?.user?.phone ||
        team?.User?.phone ||
        team?.manager?.phone ||
        team?.manager_phone ||
        '-'
    );
};

const getTeamId = (payment) => {
    const team = getPaymentTeam(payment);

    return payment?.team_id || team?.id || payment?.teamId || null;
};

const getBillingSummary = (payment) => {
    return payment?.billing_summary || payment?.billingSummary || {};
};

const getBillingLabel = (key) => {
    const labels = {
        team_registration: 'Biaya Pendaftaran Kontingen',
        kyorugi: 'Kyorugi',
        poomsae_individual: 'Poomsae Individual',
        poomsae_freestyle: 'Poomsae Freestyle',
        poomsae_pair: 'Poomsae Pair',
        poomsae_team: 'Poomsae Team',
        other: 'Lainnya',
        unknown: 'Lainnya'
    };

    return labels[key] || key || 'Lainnya';
};

const normalizeBillingBreakdown = (rawBreakdown = []) => {
    if (!Array.isArray(rawBreakdown)) return [];

    return rawBreakdown
        .map((item) => {
            const key = item.key || item.type || item.code || 'unknown';
            const qty = safeNumber(firstValue(item.qty, item.count, item.quantity));
            const amount = safeNumber(firstValue(item.amount, item.total, item.subtotal));
            const unitPrice = safeNumber(firstValue(item.unit_price, item.unitPrice, item.price, qty > 0 ? amount / qty : 0));

            return {
                key,
                label: item.label || getBillingLabel(key),
                qty,
                unitPrice,
                amount
            };
        })
        .filter(item => item.qty > 0 || item.amount > 0);
};

const getBillingAmounts = (payment) => {
    const summary = getBillingSummary(payment);

    const teamRegistrationFee = safeNumber(firstValue(
        summary.team_registration_fee,
        summary.teamRegistrationFee
    ));

    const totalAthleteAmount = safeNumber(firstValue(
        summary.total_athlete_amount,
        summary.totalAthleteAmount
    ));

    const totalBillAmount = safeNumber(firstValue(
        summary.total_registered_amount,
        summary.total_bill_amount,
        summary.totalBillAmount,
        summary.totalBill,
        payment?.total_bill_amount,
        payment?.total_registered_amount,
        payment?.total_amount
    ));

    const totalVerifiedAmount = safeNumber(firstValue(
        summary.total_verified_amount,
        summary.totalVerifiedAmount
    ));

    const outstandingAmount = safeNumber(firstValue(
        summary.outstanding_amount,
        summary.outstandingAmount
    ));

    const uploadedAmount = safeNumber(payment?.total_amount);

    return {
        teamRegistrationFee,
        totalAthleteAmount,
        totalBillAmount,
        totalVerifiedAmount,
        outstandingAmount,
        uploadedAmount,
        billingBreakdown: normalizeBillingBreakdown(
            summary.billing_breakdown ||
            summary.billingBreakdown ||
            []
        )
    };
};

const getCompetitionClass = (athlete) => {
    if (!athlete) return '-';

    if (athlete.competition_type === 'Kyorugi') {
        return athlete.weight_class || '-';
    }

    return athlete.poomsae_category || '-';
};

const getCompetitionTypeLabel = (value) => {
    if (normalizeText(value) === 'kyorugi') return 'Kyorugi';
    if (normalizeText(value) === 'poomsae') return 'Poomsae';

    return value || '-';
};

const getRegistrationTypeLabel = (value) => {
    if (normalizeText(value) === 'prestasi') return 'Prestasi';
    if (normalizeText(value) === 'festival') return 'Festival';

    return value || '-';
};

const formatDate = (dateValue, options = {}) => {
    if (!dateValue) return '-';

    try {
        return new Date(dateValue).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            ...options
        });
    } catch (error) {
        return '-';
    }
};

const AdminPayment = () => {
    const [payments, setPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [previewImage, setPreviewImage] = useState(null);

    // State Modal History
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedTeamHistory, setSelectedTeamHistory] = useState([]);
    const [selectedTeamName, setSelectedTeamName] = useState('');
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);

    // State Modal Verifikasi
    const [verifyModal, setVerifyModal] = useState({
        show: false,
        payment: null,
        status: '',
        adminNote: '',
        isSubmitting: false
    });

    // --- KONFIGURASI URL ---
    const BACKEND_URL = process.env.REACT_APP_BACKEND || '';
    const UPLOAD_DIR = '/uploads/payments/';

    // ======================================================================
    // DERIVED STATS
    // ======================================================================
    const paymentStats = useMemo(() => {
        const pending = payments.filter(payment => payment.status === 'Pending').length;
        const verified = payments.filter(payment => payment.status === 'Verified').length;
        const rejected = payments.filter(payment => payment.status === 'Rejected').length;

        const totalAmountPending = payments
            .filter(payment => payment.status === 'Pending')
            .reduce((sum, payment) => sum + safeNumber(payment.total_amount), 0);

        const totalAmountVerified = payments
            .filter(payment => payment.status === 'Verified')
            .reduce((sum, payment) => sum + safeNumber(payment.total_amount), 0);

        const pendingTeams = new Set(
            payments
                .filter(payment => payment.status === 'Pending')
                .map(payment => getTeamId(payment))
                .filter(Boolean)
        ).size;

        return {
            pending,
            verified,
            rejected,
            pendingTeams,
            totalAmountPending,
            totalAmountVerified
        };
    }, [payments]);

    // ======================================================================
    // FETCH DATA
    // ======================================================================
    const fetchPayments = async () => {
        setIsLoading(true);

        try {
            const response = await api.get('/payments');

            const data = Array.isArray(response.data)
                ? response.data
                : response.data.result || response.data.data || [];

            setPayments(data);
        } catch (error) {
            console.error('Gagal load payments', error);
            toast.error('Gagal memuat data pembayaran.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPayments();
    }, []);

    // ======================================================================
    // HANDLER VERIFIKASI
    // ======================================================================
    const openVerifyModal = (payment, status) => {
        setVerifyModal({
            show: true,
            payment,
            status,
            adminNote: status === 'Verified'
                ? 'Pembayaran diterima dan diverifikasi oleh Admin.'
                : 'Bukti pembayaran tidak valid/buram.',
            isSubmitting: false
        });
    };

    const closeVerifyModal = () => {
        if (verifyModal.isSubmitting) return;

        setVerifyModal({
            show: false,
            payment: null,
            status: '',
            adminNote: '',
            isSubmitting: false
        });
    };

    const submitVerifyPayment = async () => {
        if (!verifyModal.payment?.uuid) {
            return toast.error('Data pembayaran tidak valid.');
        }

        if (verifyModal.status === 'Rejected' && !verifyModal.adminNote.trim()) {
            return toast.warning('Alasan penolakan wajib diisi.');
        }

        setVerifyModal(prev => ({
            ...prev,
            isSubmitting: true
        }));

        try {
            await api.patch(`/payments/verify/${verifyModal.payment.uuid}`, {
                status: verifyModal.status,
                admin_note: verifyModal.adminNote
            });

            toast.success(`Pembayaran berhasil di-${verifyModal.status === 'Verified' ? 'terima' : 'tolak'}`);

            setVerifyModal({
                show: false,
                payment: null,
                status: '',
                adminNote: '',
                isSubmitting: false
            });

            fetchPayments();
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.msg || 'Gagal update status pembayaran.');

            setVerifyModal(prev => ({
                ...prev,
                isSubmitting: false
            }));
        }
    };

    // ======================================================================
    // FETCH HISTORY PER TIM
    // ======================================================================
    const openHistoryModal = async (teamId, teamName) => {
        if (!teamId) {
            return toast.warning('ID tim tidak ditemukan.');
        }

        setSelectedTeamName(teamName || '-');
        setShowHistoryModal(true);
        setIsHistoryLoading(true);
        setSelectedTeamHistory([]);

        try {
            const res = await api.get(`/payments/history/${teamId}`);

            const data = Array.isArray(res.data)
                ? res.data
                : res.data.result || res.data.data || [];

            setSelectedTeamHistory(data);
        } catch (error) {
            toast.error('Gagal memuat riwayat.');
        } finally {
            setIsHistoryLoading(false);
        }
    };

    // ======================================================================
    // FORMATTER
    // ======================================================================
    const getProofImageUrl = (proofImage) => {
        if (!proofImage) return '';

        if (String(proofImage).startsWith('http')) {
            return proofImage;
        }

        if (String(proofImage).startsWith('/uploads')) {
            return `${BACKEND_URL}${proofImage}`;
        }

        return `${BACKEND_URL}${UPLOAD_DIR}${proofImage}`;
    };

    const renderStatusBadge = (status) => {
        if (status === 'Pending') {
            return (
                <span className="badge bg-warning bg-opacity-25 text-warning border border-warning border-opacity-50 px-3 py-2 rounded-pill">
                    Menunggu
                </span>
            );
        }

        if (status === 'Verified') {
            return (
                <span className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-50 px-3 py-2 rounded-pill">
                    Lunas
                </span>
            );
        }

        if (status === 'Rejected') {
            return (
                <span className="badge bg-danger bg-opacity-25 text-danger border border-danger border-opacity-50 px-3 py-2 rounded-pill">
                    Ditolak
                </span>
            );
        }

        return (
            <span className="badge bg-light text-muted border px-3 py-2 rounded-pill">
                {status || '-'}
            </span>
        );
    };

    const renderHistoryStatusBadge = (status) => {
        if (status === 'Verified') {
            return (
                <span className="badge bg-success bg-opacity-25 text-success border border-success border-opacity-50 rounded-pill px-3">
                    LUNAS
                </span>
            );
        }

        if (status === 'Rejected') {
            return (
                <span className="badge bg-danger bg-opacity-25 text-danger border border-danger border-opacity-50 rounded-pill px-3">
                    DITOLAK
                </span>
            );
        }

        return (
            <span className="badge bg-warning bg-opacity-25 text-warning border border-warning border-opacity-50 rounded-pill px-3">
                MENUNGGU
            </span>
        );
    };

    const renderBillingBreakdown = (breakdown = [], compact = false) => {
        if (!Array.isArray(breakdown) || breakdown.length === 0) return null;

        const list = compact ? breakdown.slice(0, 3) : breakdown;

        return (
            <div className="d-flex flex-column gap-2">
                {list.map((item) => (
                    <div
                        key={item.key}
                        className="small d-flex justify-content-between align-items-center gap-2 bg-light rounded-3 px-3 py-2"
                    >
                        <span className="text-muted">
                            {item.label}
                            {' '}
                            <span className="fw-bold">
                                × {item.key === 'team_registration' ? 1 : item.qty}
                            </span>
                        </span>

                        <span className="fw-bold text-dark">
                            {formatRupiah(item.amount)}
                        </span>
                    </div>
                ))}

                {compact && breakdown.length > 3 && (
                    <div className="small text-muted fw-bold px-1">
                        +{breakdown.length - 3} rincian lainnya
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="container-fluid p-0 animate-fade-in">

            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                        <BsWalletFill className="text-primary" /> Verifikasi Pembayaran
                    </h4>
                    <p className="text-muted small mb-0">
                        Tinjau bukti transfer, total tagihan, biaya kontingen, dan biaya entry atlet.
                    </p>
                </div>

                <button
                    className="btn btn-light border rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2"
                    onClick={fetchPayments}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                        <BsClockHistory />
                    )}
                    Refresh
                </button>
            </div>

            {/* STATS RINGKAS */}
            <div className="row g-3 mb-4">
                <div className="col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white hover-up">
                        <div className="card-body p-4 d-flex align-items-center justify-content-between">
                            <div>
                                <p className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                    Menunggu Konfirmasi
                                </p>
                                <h3 className="fw-black text-warning m-0">
                                    {paymentStats.pending}
                                </h3>
                                <div className="small text-muted fw-bold mt-1">
                                    {formatRupiah(paymentStats.totalAmountPending)}
                                </div>
                                <div className="small text-muted mt-1">
                                    {paymentStats.pendingTeams} kontingen
                                </div>
                            </div>

                            <div className="bg-warning bg-opacity-25 text-warning rounded-4 d-flex align-items-center justify-content-center" style={{ width: '54px', height: '54px' }}>
                                <BsClock size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white hover-up">
                        <div className="card-body p-4 d-flex align-items-center justify-content-between">
                            <div>
                                <p className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                    Total Lunas
                                </p>
                                <h3 className="fw-black text-success m-0">
                                    {paymentStats.verified}
                                </h3>
                                <div className="small text-muted fw-bold mt-1">
                                    {formatRupiah(paymentStats.totalAmountVerified)}
                                </div>
                            </div>

                            <div className="bg-success bg-opacity-25 text-success rounded-4 d-flex align-items-center justify-content-center" style={{ width: '54px', height: '54px' }}>
                                <BsCheckCircleFill size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 col-xl-4">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white hover-up">
                        <div className="card-body p-4 d-flex align-items-center justify-content-between">
                            <div>
                                <p className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                    Ditolak
                                </p>
                                <h3 className="fw-black text-danger m-0">
                                    {paymentStats.rejected}
                                </h3>
                                <div className="small text-muted fw-bold mt-1">
                                    Perlu upload ulang
                                </div>
                            </div>

                            <div className="bg-danger bg-opacity-10 text-danger rounded-4 d-flex align-items-center justify-content-center" style={{ width: '54px', height: '54px' }}>
                                <BsXCircleFill size={24} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* TABEL PEMBAYARAN */}
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light text-muted small text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                <tr>
                                    <th className="ps-4 py-3 border-0">Tim / Kontingen</th>
                                    <th className="py-3 border-0">Tagihan & Rincian</th>
                                    <th className="py-3 border-0 text-center">Bukti Transfer</th>
                                    <th className="py-3 border-0 text-center">Status</th>
                                    <th className="py-3 border-0">Kontak Manager</th>
                                    <th className="py-3 pe-4 border-0 text-end">Aksi</th>
                                </tr>
                            </thead>

                            <tbody className="border-top-0">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status"></div>
                                            <p className="text-muted small mt-2">Memuat data pembayaran...</p>
                                        </td>
                                    </tr>
                                ) : payments.length > 0 ? (
                                    payments.map((item) => {
                                        const teamName = getTeamName(item);
                                        const managerName = getManagerName(item);
                                        const managerPhone = getManagerPhone(item);
                                        const proofImageUrl = getProofImageUrl(item.proof_image);
                                        const teamId = getTeamId(item);
                                        const billing = getBillingAmounts(item);

                                        return (
                                            <tr key={item.uuid || item.id} className="hover-up-row">
                                                <td className="ps-4 py-3">
                                                    <div className="fw-bold text-dark">{teamName}</div>

                                                    <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                                                        <small className="text-muted">
                                                            {formatDate(item.updatedAt || item.createdAt)}
                                                        </small>

                                                        <span className="text-muted">•</span>

                                                        <button
                                                            onClick={() => openHistoryModal(teamId, teamName)}
                                                            className="btn btn-sm btn-link text-decoration-none p-0 fw-bold d-flex align-items-center gap-1"
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            <BsClockHistory /> Riwayat
                                                        </button>
                                                    </div>
                                                </td>

                                                <td className="py-3">
                                                    <div className="fw-black text-dark">
                                                        {formatRupiah(billing.totalBillAmount)}
                                                    </div>

                                                    <div className="small text-muted mt-1">
                                                        Bukti transfer:{' '}
                                                        <span className="fw-bold text-primary">
                                                            {formatRupiah(billing.uploadedAmount)}
                                                        </span>
                                                    </div>

                                                    {billing.teamRegistrationFee > 0 && (
                                                        <div className="small text-muted">
                                                            Kontingen:{' '}
                                                            <span className="fw-bold text-dark">
                                                                {formatRupiah(billing.teamRegistrationFee)}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {billing.totalAthleteAmount > 0 && (
                                                        <div className="small text-muted">
                                                            Entry atlet:{' '}
                                                            <span className="fw-bold text-dark">
                                                                {formatRupiah(billing.totalAthleteAmount)}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {billing.outstandingAmount > 0 && item.status !== 'Verified' && (
                                                        <div className="small text-danger fw-bold mt-1">
                                                            Sisa setelah verifikasi saat ini: {formatRupiah(billing.outstandingAmount)}
                                                        </div>
                                                    )}

                                                    {billing.billingBreakdown.length > 0 && (
                                                        <div className="mt-2">
                                                            {renderBillingBreakdown(billing.billingBreakdown, true)}
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="text-center">
                                                    {item.proof_image ? (
                                                        <button
                                                            className="btn btn-sm btn-light text-primary border d-inline-flex align-items-center gap-2 rounded-pill px-3 shadow-sm hover-scale"
                                                            onClick={() => setPreviewImage(proofImageUrl)}
                                                        >
                                                            <BsEye /> Lihat
                                                        </button>
                                                    ) : (
                                                        <span className="badge bg-light text-muted border rounded-pill px-3 py-2">
                                                            Tidak Ada
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="text-center">
                                                    {renderStatusBadge(item.status)}
                                                </td>

                                                <td>
                                                    <div className="small text-dark fw-bold">
                                                        {managerName}
                                                    </div>
                                                    <div className="small text-muted">
                                                        {managerPhone}
                                                    </div>
                                                </td>

                                                <td className="pe-4 text-end">
                                                    {item.status === 'Pending' ? (
                                                        <div className="d-flex justify-content-end gap-2">
                                                            <button
                                                                onClick={() => openVerifyModal(item, 'Verified')}
                                                                className="btn btn-sm btn-success rounded-circle shadow-sm d-flex align-items-center justify-content-center hover-scale"
                                                                style={{ width: '35px', height: '35px' }}
                                                                title="Terima (Lunas)"
                                                            >
                                                                <BsCheckLg />
                                                            </button>

                                                            <button
                                                                onClick={() => openVerifyModal(item, 'Rejected')}
                                                                className="btn btn-sm btn-danger rounded-circle shadow-sm d-flex align-items-center justify-content-center hover-scale"
                                                                style={{ width: '35px', height: '35px' }}
                                                                title="Tolak"
                                                            >
                                                                <BsXLg />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="badge bg-light text-muted border px-3 py-1 rounded-pill">
                                                            Selesai
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 text-muted">
                                            <BsReceipt size={40} className="mb-3 opacity-25" />
                                            <h6 className="fw-bold">Belum ada data pembayaran</h6>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* MODAL VERIFIKASI */}
            {verifyModal.show && (() => {
                const billing = getBillingAmounts(verifyModal.payment);

                return (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            zIndex: 1070,
                            backgroundColor: 'rgba(15,23,42,0.55)',
                            backdropFilter: 'blur(4px)'
                        }}
                        onClick={closeVerifyModal}
                    >
                        <div
                            className="card border-0 shadow-lg animate-pop-in"
                            style={{
                                width: '90%',
                                maxWidth: '520px',
                                borderRadius: '24px'
                            }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="card-body p-4 p-md-5 text-center">
                                <div
                                    className={`rounded-circle d-inline-flex align-items-center justify-content-center mb-3 ${
                                        verifyModal.status === 'Verified'
                                            ? 'bg-success bg-opacity-10 text-success'
                                            : 'bg-danger bg-opacity-10 text-danger'
                                    }`}
                                    style={{
                                        width: '70px',
                                        height: '70px'
                                    }}
                                >
                                    {verifyModal.status === 'Verified' ? (
                                        <BsCheckLg size={34} />
                                    ) : (
                                        <BsExclamationTriangle size={34} />
                                    )}
                                </div>

                                <h5 className="fw-black text-dark mb-2">
                                    {verifyModal.status === 'Verified'
                                        ? 'Terima Pembayaran?'
                                        : 'Tolak Pembayaran?'}
                                </h5>

                                <p className="text-muted small mb-4">
                                    {verifyModal.status === 'Verified'
                                        ? 'Pembayaran akan ditandai lunas sesuai nominal bukti transfer dan tagihan tim akan diperbarui.'
                                        : 'Pembayaran akan ditolak. Manager dapat mengunggah bukti pembayaran ulang.'}
                                </p>

                                <div className="bg-light rounded-4 p-3 text-start mb-3">
                                    <div className="small text-muted fw-bold text-uppercase mb-1">
                                        Tim / Kontingen
                                    </div>
                                    <div className="fw-bold text-dark">
                                        {getTeamName(verifyModal.payment)}
                                    </div>

                                    <div className="row g-2 mt-3">
                                        <div className="col-6">
                                            <div className="bg-white rounded-3 p-3">
                                                <div className="small text-muted fw-bold text-uppercase mb-1">
                                                    Total Tagihan Tim
                                                </div>
                                                <div className="fw-black text-dark">
                                                    {formatRupiah(billing.totalBillAmount)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-6">
                                            <div className="bg-white rounded-3 p-3">
                                                <div className="small text-muted fw-bold text-uppercase mb-1">
                                                    Nominal Bukti
                                                </div>
                                                <div className="fw-black text-primary">
                                                    {formatRupiah(billing.uploadedAmount)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {(billing.teamRegistrationFee > 0 || billing.totalAthleteAmount > 0) && (
                                        <div className="mt-3 d-flex flex-column gap-1">
                                            {billing.teamRegistrationFee > 0 && (
                                                <div className="small d-flex justify-content-between">
                                                    <span className="text-muted">Biaya Kontingen</span>
                                                    <span className="fw-bold text-dark">{formatRupiah(billing.teamRegistrationFee)}</span>
                                                </div>
                                            )}

                                            {billing.totalAthleteAmount > 0 && (
                                                <div className="small d-flex justify-content-between">
                                                    <span className="text-muted">Biaya Entry Atlet</span>
                                                    <span className="fw-bold text-dark">{formatRupiah(billing.totalAthleteAmount)}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {billing.billingBreakdown.length > 0 && (
                                        <div className="mt-3">
                                            {renderBillingBreakdown(billing.billingBreakdown, false)}
                                        </div>
                                    )}
                                </div>

                                <div className="text-start mb-4">
                                    <label className="form-label small fw-bold text-muted">
                                        Catatan Admin
                                    </label>
                                    <textarea
                                        className="form-control bg-light border-0 rounded-4 px-3 py-3"
                                        rows="3"
                                        value={verifyModal.adminNote}
                                        onChange={(e) => setVerifyModal(prev => ({
                                            ...prev,
                                            adminNote: e.target.value
                                        }))}
                                        placeholder="Tambahkan catatan admin..."
                                    />
                                </div>

                                <div className="d-flex gap-2 justify-content-center">
                                    <button
                                        className="btn btn-light border rounded-pill px-4 fw-bold"
                                        onClick={closeVerifyModal}
                                        disabled={verifyModal.isSubmitting}
                                    >
                                        Batal
                                    </button>

                                    <button
                                        className={`btn rounded-pill px-4 fw-bold shadow-sm ${
                                            verifyModal.status === 'Verified'
                                                ? 'btn-success'
                                                : 'btn-danger'
                                        }`}
                                        onClick={submitVerifyPayment}
                                        disabled={verifyModal.isSubmitting}
                                    >
                                        {verifyModal.isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Memproses...
                                            </>
                                        ) : verifyModal.status === 'Verified' ? (
                                            'Ya, Terima'
                                        ) : (
                                            'Ya, Tolak'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* MODAL PREVIEW GAMBAR */}
            {previewImage && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        zIndex: 1060,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(3px)'
                    }}
                    onClick={() => setPreviewImage(null)}
                >
                    <div
                        className="position-relative bg-white p-3 rounded-4 shadow-lg animate-pop-in"
                        style={{
                            maxWidth: '90%',
                            maxHeight: '90%'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            className="btn btn-light shadow-sm text-danger position-absolute rounded-circle d-flex align-items-center justify-content-center hover-scale"
                            style={{
                                top: '-15px',
                                right: '-15px',
                                width: '40px',
                                height: '40px',
                                zIndex: 10
                            }}
                            onClick={() => setPreviewImage(null)}
                        >
                            <BsXLg />
                        </button>

                        <img
                            src={previewImage}
                            alt="Bukti Transfer"
                            className="img-fluid rounded-3"
                            style={{
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                minWidth: '300px'
                            }}
                            onError={(event) => {
                                event.target.onerror = null;
                                event.target.src = 'https://placehold.co/400x600?text=Gambar+Tidak+Ditemukan';
                                toast.error('Gagal memuat gambar bukti.');
                            }}
                        />
                    </div>
                </div>
            )}

            {/* MODAL HISTORY */}
            {showHistoryModal && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        background: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(3px)',
                        zIndex: 1050
                    }}
                >
                    <div
                        className="card border-0 shadow-lg animate-pop-in"
                        style={{
                            width: '90%',
                            maxWidth: '720px',
                            borderRadius: '20px'
                        }}
                    >
                        <div className="card-header bg-white d-flex justify-content-between align-items-center p-4 border-bottom-0">
                            <h5 className="fw-bold m-0 text-dark d-flex align-items-center gap-2">
                                <BsClockHistory className="text-primary" />
                                Riwayat Tim:
                                <span className="text-primary">{selectedTeamName}</span>
                            </h5>

                            <button
                                onClick={() => setShowHistoryModal(false)}
                                className="btn btn-sm btn-light rounded-circle d-flex align-items-center justify-content-center hover-scale"
                                style={{ width: '35px', height: '35px' }}
                            >
                                <BsXLg />
                            </button>
                        </div>

                        <div className="card-body p-0 custom-scrollbar" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                            {isHistoryLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary"></div>
                                </div>
                            ) : selectedTeamHistory.length > 0 ? (
                                <div className="list-group list-group-flush pb-3">
                                    {selectedTeamHistory.map((historyItem, idx) => {
                                        const isPayment = historyItem.type === 'payment';
                                        const data = historyItem.data || {};
                                        const members = getEntryMembers(data);
                                        const displayName = getDisplayName(data);
                                        const classLabel = getCompetitionClass(data);
                                        const typeLabel = getRegistrationTypeLabel(data.type);

                                        return (
                                            <div
                                                key={idx}
                                                className="list-group-item px-4 py-3 border-top border-bottom-0 d-flex align-items-start gap-3 hover-up-row"
                                            >
                                                <div
                                                    className={`mt-1 flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center ${
                                                        isPayment
                                                            ? 'bg-success bg-opacity-25 text-success'
                                                            : 'bg-primary bg-opacity-25 text-primary'
                                                    }`}
                                                    style={{
                                                        width: '45px',
                                                        height: '45px'
                                                    }}
                                                >
                                                    {isPayment ? <BsReceipt size={20} /> : <BsPersonPlusFill size={20} />}
                                                </div>

                                                <div className="flex-grow-1 overflow-hidden">
                                                    <div className="d-flex align-items-start justify-content-between gap-3 mb-1">
                                                        <div className="overflow-hidden">
                                                            <h6 className="fw-bold mb-0 text-dark">
                                                                {isPayment ? 'Pembayaran Tagihan' : `Entry: ${displayName}`}
                                                            </h6>

                                                            {!isPayment && members.length > 1 && (
                                                                <div className="small text-muted mt-1">
                                                                    {members.length} nama dalam 1 entry
                                                                </div>
                                                            )}
                                                        </div>

                                                        <small className="text-muted fw-medium flex-shrink-0">
                                                            {formatDate(historyItem.date, { year: undefined })}
                                                        </small>
                                                    </div>

                                                    {isPayment ? (
                                                        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                                            {renderHistoryStatusBadge(data.status)}
                                                            <span className="text-dark small fw-bold">
                                                                {formatRupiah(data.total_amount)}
                                                            </span>

                                                            {data.admin_note && (
                                                                <span className="small text-muted d-flex align-items-center gap-1">
                                                                    <BsInfoCircle /> {data.admin_note}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                                            <span className="badge bg-light text-muted border rounded-pill px-3">
                                                                {getCompetitionTypeLabel(data.competition_type)}
                                                            </span>

                                                            <span className="badge bg-light text-dark border rounded-pill px-3">
                                                                {typeLabel}
                                                            </span>

                                                            <span className="badge bg-light text-dark border rounded-pill px-3">
                                                                {data.division || '-'} / {classLabel}
                                                            </span>

                                                            {members.length > 1 && (
                                                                <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill px-3">
                                                                    Pair/Team
                                                                </span>
                                                            )}

                                                            <span
                                                                className={`badge ${
                                                                    historyItem.status === 'Unpaid'
                                                                        ? 'bg-danger bg-opacity-25 text-danger border-danger'
                                                                        : 'bg-success bg-opacity-25 text-success border-success'
                                                                } border rounded-pill px-3`}
                                                            >
                                                                {historyItem.status === 'Unpaid' ? 'Belum Bayar' : 'Tercover / Lunas'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-5 text-muted">
                                    Tidak ada data riwayat untuk tim ini.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* CSS Internal */}
            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                .animate-pop-in { animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }

                .fw-black { font-weight: 900; }

                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.05); }

                .hover-up { transition: transform 0.2s, box-shadow 0.2s; }
                .hover-up:hover { transform: translateY(-4px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.08)!important; }

                .hover-up-row { transition: background-color 0.2s; }
                .hover-up-row:hover { background-color: #f8f9fa !important; }

                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #adb5bd; }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes popIn {
                    from { opacity: 0; transform: scale(0.9); }
                    to { opacity: 1; transform: scale(1); }
                }
            `}</style>
        </div>
    );
};

export default AdminPayment;